import {
  HttpErrorResponse,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { catchError, switchMap, take, throwError, timeout } from 'rxjs';
import { AuthStore } from '../store/auth/auth.store';
import { ReauthService, TOKEN_TIMEOUT_MS } from '../services/reauth.service';

/**
 * Adds the user's access token to API requests. The token comes from Auth0 for
 * each request rather than from the one read at startup: the SDK returns its
 * cached token and only refreshes it when it is about to expire, so a long
 * session keeps sending a valid token.
 *
 * When no token can be obtained, the session cannot be recovered without a
 * login: the request fails with a 401 and the user is sent through
 * reauthentication.
 */
export const httpHeaderInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const auth0Service = inject(Auth0Service);
  const reauthService = inject(ReauthService);

  // No session was established at startup, or the request is not for an API.
  if (!authStore.token() || exemptUrls(req)) {
    return next(req);
  }

  return auth0Service.getAccessTokenSilently().pipe(
    timeout(TOKEN_TIMEOUT_MS),
    take(1),
    catchError(() => {
      reauthService.reauthenticate();
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 401,
            statusText: 'Unauthorized',
            url: req.urlWithParams
          })
      );
    }),
    switchMap((token) => {
      if (token !== authStore.token()) {
        authStore.setToken(token);
      }
      return next(withToken(req, token));
    })
  );
};

/** `req` with `token` as its bearer token. */
export function withToken(
  req: HttpRequest<unknown>,
  token: string
): HttpRequest<unknown> {
  let headers: HttpHeaders = req.headers;
  if (headers.has('no-default-interceptor')) {
    headers = headers.delete('no-default-interceptor');
  }
  return req.clone({
    headers: headers.set('Authorization', `Bearer ${token}`)
  });
}

const exemptUrls = (req: HttpRequest<unknown>) => {
  return (
    req.url.includes('oauth') ||
    req.url.includes('config.json') ||
    /^\/?assets\//.test(req.url)
  );
};
